import { useState, useEffect } from 'react';
import { Plus, Trash2, Save, Upload, Eye, EyeOff, Star, ChevronDown, ChevronUp, Search } from 'lucide-react';
import RichTextEditor from '../common/RichTextEditor';

import { API_URL } from '../../config/api';

const MAX_FEATURED = 3;
const CHAR_LIMITS = {
    title: 100,
    excerpt: 200,
    content: 10000,
    meta_title: 70,
    meta_description: 160
};

const EMPTY_FORM = {
    title: '',
    excerpt: '',
    content: '',
    cover_image_url: '',
    category: '',
    is_published: false,
    is_featured: false,
    meta_title: '',
    meta_description: '',
    og_image_url: ''
};

const BlogManager = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [showForm, setShowForm] = useState(false);

    // Form state lifted to parent
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [showSeo, setShowSeo] = useState(false);
    const [uploadingCover, setUploadingCover] = useState(false);

    useEffect(() => { fetchPosts(); }, []);

    const fetchPosts = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/blog-posts/all`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) setPosts(data.data || []);
        } catch (error) {
            setMessage({ type: 'error', text: 'Error al cargar posts' });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!formData.title) {
            setMessage({ type: 'error', text: 'El título es requerido' });
            return;
        }

        // Prevent obvious limit breach in UI before request.
        if (formData.is_featured) {
            const featuredCount = posts.filter(p => p.is_featured && p.id !== formData.id).length;
            if (featuredCount >= MAX_FEATURED) {
                setMessage({ type: 'error', text: `Máximo ${MAX_FEATURED} artículos destacados` });
                return;
            }
        }

        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            const isNew = !formData.id;
            const url = isNew ? `${API_URL}/blog-posts` : `${API_URL}/blog-posts/${formData.id}`;

            const response = await fetch(url, {
                method: isNew ? 'POST' : 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();
            if (data.success) {
                setMessage({ type: 'success', text: 'Guardado correctamente' });
                fetchPosts();
                closeForm();
            } else {
                setMessage({ type: 'error', text: data.message });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Error al guardar' });
        } finally {
            setSaving(false);
            setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Eliminar este post permanentemente?')) return;
        try {
            const token = localStorage.getItem('token');
            await fetch(`${API_URL}/blog-posts/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            fetchPosts();
            setMessage({ type: 'success', text: 'Post eliminado' });
        } catch (error) {
            setMessage({ type: 'error', text: 'Error al eliminar' });
        }
    };

    const togglePublish = async (post) => {
        const token = localStorage.getItem('token');
        await fetch(`${API_URL}/blog-posts/${post.id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ is_published: !post.is_published })
        });
        fetchPosts();
    };

    const openForm = (post = null) => {
        if (post) {
            setFormData({ ...EMPTY_FORM, ...post });
        } else {
            setFormData(EMPTY_FORM);
        }
        setShowSeo(false);
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false);
        setFormData(EMPTY_FORM);
    };

    const handleImageUpload = async (e, field = 'cover_image_url') => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploadingCover(true);
        setMessage({ type: 'info', text: 'Subiendo imagen...' });

        const uploadData = new FormData();
        uploadData.append('file', file);
        uploadData.append('category', 'blog');

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: uploadData
            });

            const result = await response.json();
            if (result.success) {
                const fullUrl = result.data.url.startsWith('http')
                    ? result.data.url
                    : `${API_URL}${result.data.url}`;
                setFormData(prev => ({ ...prev, [field]: fullUrl }));
                setMessage({ type: 'success', text: 'Imagen subida' });
            } else {
                setMessage({ type: 'error', text: result.message || 'Error al subir' });
            }
        } catch (error) {
            console.error('Upload error:', error);
            setMessage({ type: 'error', text: 'Error de conexión' });
        } finally {
            setUploadingCover(false);
        }
    };

    const updateField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    if (loading) return <div className="loading-state">Cargando blog...</div>;

    return (
        <div className="blog-manager">
            <div className="manager-header">
                <div className="header-info">
                    <h3>Blog</h3>
                    <span className="count-badge">{posts.filter(p => p.is_published).length} publicados</span>
                    <span className="count-badge draft">{posts.filter(p => !p.is_published).length} borradores</span>
                </div>
                <button className="btn btn-primary" onClick={() => openForm()}>
                    <Plus size={16} /> Nuevo Artículo
                </button>
            </div>

            {message.text && <div className={`message ${message.type}`}>{message.text}</div>}

            {/* Form */}
            {showForm && (
                <div className="item-form blog-form">
                    <div className="form-grid">
                        {/* Title */}
                        <div className="form-group full-width">
                            <label>
                                Título del Artículo
                                <span className="char-counter">{(formData.title || '').length}/{CHAR_LIMITS.title}</span>
                            </label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={(e) => updateField('title', e.target.value.slice(0, CHAR_LIMITS.title))}
                                placeholder="Título del artículo"
                                className="large-input"
                            />
                        </div>

                        {/* Category */}
                        <div className="form-group">
                            <label>Categoría</label>
                            <input
                                type="text"
                                value={formData.category}
                                onChange={(e) => updateField('category', e.target.value)}
                                placeholder="Ej: Genética, Salud"
                            />
                        </div>

                        {/* Status */}
                        <div className="form-group checkbox-row">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={formData.is_published}
                                    onChange={(e) => updateField('is_published', e.target.checked)}
                                />
                                <Eye size={16} /> Publicado
                            </label>
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={formData.is_featured}
                                    onChange={(e) => updateField('is_featured', e.target.checked)}
                                />
                                <Star size={16} /> Destacado
                            </label>
                        </div>

                        {/* Excerpt */}
                        <div className="form-group full-width">
                            <label>
                                Extracto
                                <span className="char-counter">{(formData.excerpt || '').length}/{CHAR_LIMITS.excerpt}</span>
                            </label>
                            <textarea
                                value={formData.excerpt}
                                onChange={(e) => updateField('excerpt', e.target.value.slice(0, CHAR_LIMITS.excerpt))}
                                rows={2}
                                placeholder="Breve resumen"
                            />
                        </div>

                        {/* Content */}
                        <div className="form-group full-width">
                            <label>Contenido</label>
                            <RichTextEditor
                                value={formData.content}
                                onChange={(content) => updateField('content', content)}
                                height={350}
                                placeholder="Contenido del artículo..."
                            />
                        </div>

                        {/* Cover Image */}
                        <div className="form-group">
                            <label>Imagen de Portada {uploadingCover && '(subiendo...)'}</label>
                            <div className="image-upload-area">
                                {formData.cover_image_url ? (
                                    <img src={formData.cover_image_url} alt="Preview" className="cover-preview" />
                                ) : (
                                    <div className="upload-placeholder">1200x630 px</div>
                                )}
                                <label className="upload-btn">
                                    <Upload size={16} /> {uploadingCover ? 'Subiendo...' : 'Subir'}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleImageUpload(e, 'cover_image_url')}
                                        hidden
                                        disabled={uploadingCover}
                                    />
                                </label>
                            </div>
                        </div>

                        {/* SEO Toggle */}
                        <div className="form-group full-width seo-section">
                            <button type="button" className="seo-toggle" onClick={() => setShowSeo(!showSeo)}>
                                <Search size={16} /> Configuración SEO
                                {showSeo ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                            {showSeo && (
                                <div className="seo-fields">
                                    <div className="form-group">
                                        <label>
                                            Meta Título
                                            <span className="char-counter">{(formData.meta_title || '').length}/{CHAR_LIMITS.meta_title}</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.meta_title || ''}
                                            onChange={(e) => updateField('meta_title', e.target.value.slice(0, CHAR_LIMITS.meta_title))}
                                            placeholder={formData.title || 'Título para Google'}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>
                                            Meta Descripción
                                            <span className="char-counter">{(formData.meta_description || '').length}/{CHAR_LIMITS.meta_description}</span>
                                        </label>
                                        <textarea
                                            value={formData.meta_description || ''}
                                            onChange={(e) => updateField('meta_description', e.target.value.slice(0, CHAR_LIMITS.meta_description))}
                                            rows={2}
                                            placeholder="Descripción para resultados de Google"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="form-actions">
                        <button className="btn btn-secondary" onClick={closeForm}>Cancelar</button>
                        <button
                            className="btn btn-primary"
                            onClick={handleSave}
                            disabled={saving || !formData.title}
                        >
                            <Save size={16} /> {saving ? 'Guardando...' : 'Guardar'}
                        </button>
                    </div>
                </div>
            )}

            {/* Posts List */}
            <div className="posts-list">
                {posts.map((post) => (
                    <div key={post.id} className={`post-card ${!post.is_published ? 'draft' : ''}`}>
                        <div className="post-cover">
                            {post.cover_image_url ? (
                                <img src={post.cover_image_url} alt={post.title} />
                            ) : (
                                <div className="no-cover">📝</div>
                            )}
                        </div>
                        <div className="post-info">
                            <div className="post-header">
                                <h4>{post.title}</h4>
                                {post.is_featured && <Star size={14} className="featured-icon" />}
                            </div>
                            <p>{post.excerpt}</p>
                            <div className="post-meta">
                                <span className={`status ${post.is_published ? 'published' : 'draft'}`}>
                                    {post.is_published ? 'Publicado' : 'Borrador'}
                                </span>
                                {post.category && <span className="category">{post.category}</span>}
                            </div>
                        </div>
                        <div className="post-actions">
                            <button onClick={() => togglePublish(post)} title={post.is_published ? 'Despublicar' : 'Publicar'}>
                                {post.is_published ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                            <button onClick={() => openForm(post)}>✏️</button>
                            <button onClick={() => handleDelete(post.id)} className="danger"><Trash2 size={16} /></button>
                        </div>
                    </div>
                ))}

                {posts.length === 0 && (
                    <div className="empty-state">No hay artículos. Crea el primero.</div>
                )}
            </div>
        </div>
    );
};

export default BlogManager;
